import {
  patientIdentifierKeys,
  useAddPatientIdentifier,
  usePatientIdentifier,
  useRemovePatientIdentifier
} from '@/features/requests/api/patientIdentifiers.api';
import useRequestFormStore from '../store';
import Drawer from '@oncolens/oncolens-ui/lib/components/Common/Drawer/Drawer';
import clsx from 'clsx';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import shallow from 'zustand/shallow';
import OncolensToast from '@/components/Elements/Toast/Toast';
import { biomarkerKeys } from '../Biomarkers/biomarkers.keys';
import { useBiomarkerContext } from './PatientBiomarkerForm';
import LabResultCard from './LabMatchCard';
import { usePanelOrdersByPatientDemographics } from '@/features/requests/api/labMatchDrawer.api';

const LabMatchDrawer = () => {
  const context = useBiomarkerContext();
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((open) => !open);
  const [firstName, lastName, dateOfBirth, patientId, organizationId] = useRequestFormStore(
    (store) => [
      store.patient?.firstName,
      store.patient?.lastName,
      store.patient?.dateOfBirth,
      store.patient?.id,
      store.patient?.organizationId
    ],
    shallow
  );

  const { data } = usePanelOrdersByPatientDemographics(
    organizationId,
    firstName,
    lastName,
    dateOfBirth,
    {
      enabled: !!patientId
    }
  );

  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const addPatientIdentifier = useAddPatientIdentifier();
  const removePatientIdentifier = useRemovePatientIdentifier();
  const resetQueries = () => {
    queryClient.invalidateQueries(biomarkerKeys.panels(patientId));
    queryClient.invalidateQueries(patientIdentifierKeys.get(patientId, 'external-patient-id'));
  };

  const handleAssign = (carisPatientId: string, panelId?: string) => {
    setIsSaving(true);

    addPatientIdentifier
      .mutateAsync({
        idType: 'external-patient-id',
        idValue: carisPatientId,
        patientId
      })
      .then(() => {
        setIsSaving(false);
        OncolensToast.success('Result successfully assigned.');
        resetQueries();
        if (panelId) {
          context.setActiveIds((previous) => ({
            ...previous,
            isEditingPanel: false,
            selectedPanelId: panelId
          }));
        }
        toggle();
      })
      .catch((error) => {
        setIsSaving(false);
        console.error(error);
        OncolensToast.error('Patient could not be assigned. Try again.');
      });
  };

  const handleUnassign = (patientId: string, carisPatientId: string) => {
    setIsSaving(true);

    removePatientIdentifier
      .mutateAsync({ patientId, value: carisPatientId })
      .then(() => {
        setIsSaving(false);
        OncolensToast.success('External labs successfully unassigned.');
        resetQueries();
        context.setActiveIds((previous) => ({
          ...previous,
          selectedPanelId: null
        }));
      })
      .catch((error) => {
        setIsSaving(false);
        console.error(error);
        OncolensToast.error('External labs could not be unassigned. Try again.');
      });
  };

  return data && data.length ? (
    <>
      <LabMatchAssign toggle={toggle} />
      <Drawer show={open} toggle={toggle}>
        <Drawer.Title title="Potential Lab Matches" />
        <Drawer.Content>
          {data.map((match) => (
            <LabResultCard
              key={match.patient.externalId}
              {...match.patient}
              labs={match.panelOrders}
              testSelector={match.patient.externalId}
              showBorder
              onButtonClick={() =>
                handleAssign(
                  match.patient.externalId,
                  match.panelOrders.length > 0 ? match.panelOrders[0].id : undefined
                )
              }
              isButtonDisabled={isSaving}
            />
          ))}
        </Drawer.Content>
      </Drawer>
    </>
  ) : null;
};

interface LabMatchAssignProps {
	toggle: () => void;
}

const LabMatchAssign = ({ toggle }: LabMatchAssignProps) => {
  return (
    <div className="self-end pl-5 ml-auto text-sm text-right">
      <p className="mb-2 text-sm font-light">View and assign potential biomarker matches</p>
      <button
        className={clsx(
          'py-1 px-1.5 inline-block whitespace-nowrap shadow-md rounded-md bg-gradient-to-br text-white from-sky-600 to-sky-700'
        )}
        onClick={toggle}
      >
				Review & Assign
      </button>
    </div>
  );
};

interface LabMatchUnassignProps {
	onUnassign: () => void;
}

const LabMatchUnassign = ({ onUnassign }: LabMatchUnassignProps) => {
  return (
    <div className="self-end pl-5 ml-auto text-sm text-right">
      <p className="mb-2 text-sm font-light">External labs have been attached to this patient</p>
      <button
        className="py-1 px-1.5 inline-block whitespace-nowrap shadow-md rounded-md bg-gradient-to-br text-white from-red-600 to-red-700 hover:from-red-500 hover:to-red-600"
        onClick={onUnassign}
      >
				Unassign
      </button>
    </div>
  );
};

export { LabMatchDrawer as default };
